<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { type Component, computed } from 'vue'

import { CheckInCircleBlackIcon } from '../../assets'
import {
  DiscordIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  PatreonIcon,
  PinterestIcon,
  SnapchatIcon,
  TelegramIcon,
  TiktokIcon,
  TumblrIcon,
  TwitterIcon,
  VimeoIcon,
  WebsiteIcon,
  WhatsappIcon,
  YoutubeIcon,
} from '../../assets/icons/socials'

const props = withDefaults(
  defineProps<{
    platform: string
    size?: number
    /** Badges the icon with a check, as a connected account is shown elsewhere. */
    isConnected?: boolean
  }>(),
  { size: 14, isConnected: false },
)

/** Proportions of the connected badge on the 24px creator-list icon: a 14px check, 4px out. */
const BADGE_RATIO = 14 / 24
const BADGE_OFFSET_RATIO = 4 / 24

const PLATFORM_ICONS: Record<string, Component> = {
  discord: DiscordIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  other: WebsiteIcon,
  patreon: PatreonIcon,
  pinterest: PinterestIcon,
  snapchat: SnapchatIcon,
  telegram: TelegramIcon,
  tiktok: TiktokIcon,
  tumblr: TumblrIcon,
  twitter: TwitterIcon,
  vimeo: VimeoIcon,
  website: WebsiteIcon,
  whatsapp: WhatsappIcon,
  youtube: YoutubeIcon,
}

const icon = computed(() => PLATFORM_ICONS[props.platform.toLowerCase()] ?? WebsiteIcon)

// The check is only a picture, so the state goes into the name as well.
const label = computed(() => (props.isConnected ? `${props.platform} (connected)` : props.platform))

const badgeSize = computed(() => Math.round(props.size * BADGE_RATIO))
const badgeOffset = computed(() => `${-Math.round(props.size * BADGE_OFFSET_RATIO)}px`)
</script>

<template>
  <span role="img" :aria-label="label" :title="label" class="platform-icon">
    <ElIcon :size="size" aria-hidden="true" class="platform-icon__glyph">
      <component :is="icon" />
    </ElIcon>
    <ElIcon
      v-if="isConnected"
      :size="badgeSize"
      aria-hidden="true"
      class="platform-icon__connected"
      :style="{ top: badgeOffset, right: badgeOffset }"
    >
      <CheckInCircleBlackIcon />
    </ElIcon>
  </span>
</template>

<style scoped lang="scss">
.platform-icon {
  display: inline-flex;
  position: relative;
}

.platform-icon__glyph {
  :deep(path) {
    fill: currentColor;
  }
}

/* Separates the badge from the platform glyph. */
.platform-icon__connected {
  position: absolute;
  background-color: var(--surface);
  border-radius: 50%;
}
</style>
